import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { ELECTRIC_TOLLS, EMPTY_INVOICE, invoiceFieldLabel, prepareReviewedInput, useInvoiceV2Enabled } from '../../services/invoiceV2';
import { invoiceInputClass as inputClass, invoiceLabelClass as labelClass } from './InvoicePresentation';
import { summarizeCase } from './invoiceDisplay';
import useInvoiceTariffCatalog from '../../services/useInvoiceTariffCatalog';
import useInvoiceConfirmation from './useInvoiceConfirmation';
import useInvoiceNotifications from './useInvoiceNotifications';
import invoiceFailureMessage from './invoiceFailureMessage';
import { changeInvoicePeriod, invoicePeriod, invoiceReferenceMode, invoiceReviewState } from '../../services/invoiceReview';
import { emailRequestId, validInvoiceEmail, validInvoiceSubject } from '../../services/invoiceEmail';
import { invoiceUploadError } from '../../services/invoiceUpload';

const ROOT = '/api/v2/invoices';
const DEFAULT_CASE_LIST = { page: 1, page_size: 10, sort_by: 'updated_at', sort_dir: 'desc', q: '', status: '', include_archived: false };

export default function useInvoiceWorkspace({ onOpenChange, standalone = false, initialAttachment = null }) {
    const { authFetch } = useAuth();
    const enabled = useInvoiceV2Enabled();
    const [open, setOpen] = useState(standalone);
    const [screen, setScreen] = useState('home');
    const [loading, setLoading] = useState(true);
    const [cases, setCases] = useState([]);
    const [caseScope, setCaseScope] = useState('mine');
    const [caseList, setCaseList] = useState(DEFAULT_CASE_LIST);
    const [casePagination, setCasePagination] = useState({ page: 1, page_size: 10, total: 0, pages: 1 });
    const [listReload, setListReload] = useState(0);
    const [record, setRecord] = useState(null);
    const [draft, setDraft] = useState(EMPTY_INVOICE);
    const [title, setTitle] = useState('');
    const [dirty, setDirty] = useState(false);
    const [busy, setBusy] = useState(false);
    const [fields, setFields] = useState({});
    const [referenceMode, setReferenceMode] = useState('total');
    const [periodMode, setPeriodMode] = useState('fechas');
    const [reviewAttempted, setReviewAttempted] = useState(false);
    const [pendingAttachment, setPendingAttachment] = useState(initialAttachment);
    const rootRef = useRef(null);
    const operationRef = useRef(false);
    const emailAttempt = useRef(null);
    const invoiceRequestAttempt = useRef(null);
    const listRequest = useRef(0);
    const exclusivePeriodEnd = useRef(false);
    const { confirmation, requestConfirmation, resolveConfirmation } = useInvoiceConfirmation();
    const { notifications, notify, dismissNotification, clearNotifications } = useInvoiceNotifications();

    const api = useCallback(async (path, options = {}) => {
        const response = await authFetch(ROOT + path, options);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            const failure = new Error(data.error || 'No se ha podido completar la operación');
            failure.fields = data.fields || {};
            failure.tariffErrors = data.errores_tarifas || [];
            throw failure;
        }
        return data;
    }, [authFetch]);
    const json = (method, value) => ({ method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(value) });
    const updateCaseSummary = value => setCases(previous => {
        const others = previous.filter(item => item.id !== value.id);
        return (caseScope === 'mine' && value.participated_by_me === false) || (value.archived_at && !caseList.include_archived)
            ? others : [summarizeCase(value), ...others].slice(0, 10);
    });
    const apply = (value, { preserveReviewOptions = false } = {}) => {
        setRecord(value);
        setDraft({ ...EMPTY_INVOICE, ...value.reviewed_input });
        setDirty(false);
        if (!preserveReviewOptions) {
            exclusivePeriodEnd.current = invoicePeriod(value.reviewed_input || {}).exclusiveEnd;
            setReferenceMode(invoiceReferenceMode(value.reviewed_input));
            setPeriodMode(value.reviewed_input?.dias_facturados != null && value.reviewed_input.dias_facturados !== '' ? 'dias' : 'fechas');
            setReviewAttempted(false);
        }
        updateCaseSummary(value);
    };
    const perform = async operation => {
        if (operationRef.current) return;
        operationRef.current = true;
        setBusy(true); setFields({});
        try { return await operation(); }
        catch (failure) {
            setFields(failure.fields || {});
            notify(invoiceFailureMessage(failure), 'red', [
                ...Object.entries(failure.fields || {}).map(([key, value]) => `${invoiceFieldLabel(key)} · ${value}`),
                ...(failure.tariffErrors || []).map(item => `Tarifa ${item.tarifa_id} · ${item.error}`),
            ]);
        }
        finally { operationRef.current = false; setBusy(false); }
    };
    const refreshCases = useCallback(async (options = {}) => {
        const generation = ++listRequest.current;
        setLoading(true);
        try {
            const params = new URLSearchParams({ ...caseList, scope: caseScope });
            const data = await api(`/cases?${params}`, options);
            if (generation === listRequest.current) {
                if (!Array.isArray(data.cases) || !data.pagination) throw new Error('No se ha podido recuperar el listado de gestiones');
                setCases(data.cases);
                setCasePagination(data.pagination);
            }
        } catch (failure) {
            if (generation === listRequest.current) {
                // Never show rows from a different page, sort or search after a failure.
                setCases([]);
                setCasePagination({ page: 1, page_size: 10, total: 0, pages: 1 });
                throw failure;
            }
        } finally { if (generation === listRequest.current) setLoading(false); }
    }, [api, caseScope, caseList]);
    const reloadCases = () => setListReload(previous => previous + 1);
    useEffect(() => {
        const controller = new AbortController();
        if (enabled && open && screen === 'cases') refreshCases({ signal: controller.signal }).catch(failure => notify(failure.message, 'red'));
        return () => { listRequest.current += 1; controller.abort(); };
    }, [refreshCases, enabled, open, screen, notify, listReload]);
    const changeCaseList = patch => {
        listRequest.current += 1;
        setLoading(true);
        setCaseList(previous => ({ ...previous, ...patch, page: patch.page ?? 1 }));
    };
    const showCases = scope => {
        if (scope !== caseScope) {
            listRequest.current += 1;
            setCases([]); setLoading(true); setCaseScope(scope); setCaseList(DEFAULT_CASE_LIST);
            setCasePagination({ page: 1, page_size: 10, total: 0, pages: 1 });
        }
        setListReload(previous => previous + 1);
        setScreen('cases');
    };
    useEffect(() => {
        if (!enabled || !open) clearNotifications();
    }, [enabled, open, clearNotifications]);
    useEffect(() => {
        onOpenChange?.(enabled && open);
        return () => onOpenChange?.(false);
    }, [enabled, open, onOpenChange]);
    useEffect(() => {
        const guard = event => { if (dirty) { event.preventDefault(); event.returnValue = ''; } };
        window.addEventListener('beforeunload', guard);
        return () => window.removeEventListener('beforeunload', guard);
    }, [dirty]);
    useEffect(() => {
        const scrollArea = rootRef.current?.closest('.esave-standalone, #console-invoices');
        if (scrollArea) scrollArea.scrollTop = 0;
    }, [screen]);

    const change = (key, value, period) => {
        setDraft(previous => {
            const next = { ...previous, [key]: period ? { ...previous[key], [period]: value } : value };
            if (key === 'tarifa_atr' && ELECTRIC_TOLLS.includes(value)) {
                ['consumos_kwh', 'precios_energia_eur_kwh', 'reparto_consumo_pct', 'potencias_kw', 'precios_potencia'].forEach(map => {
                    if (next[map] == null) return;
                    const count = value === '2.0TD' ? ['potencias_kw', 'precios_potencia'].includes(map) ? 2 : 3 : 6;
                    next[map] = Object.fromEntries(Object.entries(next[map]).filter(([p]) => Array.from({ length: count }, (_, i) => `P${i + 1}`).includes(p)));
                });
            }
            return next;
        });
        setFields(previous => Object.fromEntries(Object.entries(previous).filter(([field]) => field !== key && field !== `${key}.${period}`)));
        setDirty(true);
    };
    const changePeriod = (key, value) => {
        setDraft(previous => changeInvoicePeriod(previous, key, value, periodMode, exclusivePeriodEnd.current));
        setFields(previous => Object.fromEntries(Object.entries(previous).filter(([key]) => !['fecha_inicio', 'fecha_fin', 'dias_facturados'].includes(key))));
        setDirty(true);
    };
    const choosePeriodMode = mode => {
        setPeriodMode(mode);
        if (mode === 'dias' && (draft.dias_facturados == null || draft.dias_facturados === '')) {
            const { days } = invoicePeriod(draft);
            if (Number.isInteger(days) && days >= 1 && days <= 366) {
                setDraft(previous => ({ ...previous, dias_facturados: days }));
                setDirty(true);
            }
        }
    };
    const chooseReferenceMode = mode => { setReferenceMode(mode); setDirty(true); };
    const save = async () => {
        const value = await api(`/cases/${record.id}`, json('PUT', {
            version: record.version, reviewed_input: prepareReviewedInput(draft, { referenceMode }),
        }));
        apply(value, { preserveReviewOptions: true });
        return value;
    };
    const readDocument = async (saved, documentId) => {
        setScreen('source');
        apply({ ...saved, active_document_id: documentId, reviewed_input: {}, extraction: null,
            comparison: null, selected_offer: null, proposal: null });
        try {
            const value = await api(`/cases/${saved.id}/documents/${documentId}/extract`, json('POST', { version: saved.version }));
            apply(value);
            const isGas = value.reviewed_input?.tipo_suministro === 'gas' || /^RL/i.test(value.reviewed_input?.tarifa_atr || '');
            const complete = value.extraction?.result.is_valid;
            notify(isGas ? 'Esta factura es de gas. Sube una factura eléctrica para continuar.' : complete
                ? 'Lectura completada. Revisa los datos antes de comparar.'
                : 'Lectura parcial. Completa o corrige los datos antes de comparar.', isGas || !complete ? 'amber' : 'green');
        } catch (failure) {
            // The server persisted the pending document before contacting OCR.
            // Refresh after a timeout so another invoice's values cannot remain.
            try { apply(await api(`/cases/${saved.id}`)); }
            catch { /* Keep the local pending state if recovery is also offline. */ }
            throw failure;
        }
    };
    const upload = async (file, source = 'local', target = record, onStored) => {
        if (!file) return;
        const pages = Array.isArray(file) ? file : null;
        const failure = invoiceUploadError(pages || [file], Boolean(pages));
        if (failure) throw new Error(failure);
        const filename = pages ? `${pages.length} fotos · ${pages[0].name}` : file.name;
        if (!target && !await confirmDiscard()) return false;
        if (target && (Object.keys(target.reviewed_input || {}).length || dirty) && !await requestConfirmation({
            title: 'Leer una nueva factura',
            message: 'La lectura de esta factura sustituirá los datos de revisión de la gestión actual.',
            filename,
            confirmLabel: 'Sustituir y leer factura',
        })) return false;
        const saved = target ? dirty ? await save() : target : await api('/cases', json('POST', { titulo: filename.substring(0, 200), title_source: 'invoice' }));
        apply(saved);
        const form = new FormData();
        if (pages) pages.forEach(page => form.append('pages', page));
        else form.append('file', file);
        form.append('source', source);
        const value = await api(`/cases/${saved.id}/documents`, { method: 'POST', body: form });
        apply(value.case);
        // A reading failure must retry the saved original, without another import.
        onStored?.();
        await readDocument(value.case, value.document_id);
        return true;
    };
    const downloadBlob = (blob, filename) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a'); link.href = url; link.download = filename; link.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };
    const download = async doc => {
        const response = await authFetch(`${ROOT}/cases/${record.id}/documents/${doc.id}`);
        if (!response.ok) throw new Error('No se ha podido recuperar el original');
        downloadBlob(await response.blob(), doc.filename);
    };
    const sendProposal = (recipientEmail, subject) => perform(async () => {
        const proposal = record?.proposal;
        if (!proposal?.html || !validInvoiceEmail(recipientEmail) || !validInvoiceSubject(subject)) return;
        subject = subject.trim();
        if (!await requestConfirmation({ kind: 'email', title: 'Enviar oferta por email',
            message: 'Se enviará la oferta que has revisado a esta dirección:', filename: recipientEmail,
            detail: `Asunto: ${subject}`, confirmLabel: 'Confirmar envío',
        })) return;
        const key = `${record.id}:${proposal.id}:${recipientEmail}:${subject}`;
        if (emailAttempt.current?.key !== key) emailAttempt.current = { key, id: emailRequestId() };
        const response = await authFetch(`${ROOT}/cases/${record.id}/proposal/send`, json('POST', {
            version: record.version, proposal_id: proposal.id, recipient_email: recipientEmail,
            request_id: emailAttempt.current.id, confirmed: true, subject,
        }));
        const result = await response.json().catch(() => ({}));
        if (result.email_send) {
            const receipt = result.email_send;
            setRecord(previous => previous?.id === record.id ? { ...previous,
                email_sends: [receipt, ...(previous.email_sends || []).filter(item => item.id !== receipt.id)].slice(0, 20),
            } : previous);
            if (['accepted', 'rejected'].includes(receipt.status)) emailAttempt.current = null;
        }
        if (!response.ok) throw new Error(result.error || 'No se ha podido confirmar el envío. Revisa su estado antes de reenviar.');
        notify(`ZeptoMail ha aceptado el envío a ${recipientEmail}.`);
    });
    const requestInvoice = values => perform(async () => {
        if (!validInvoiceEmail(values.recipient_email) || !validInvoiceSubject(values.subject)) return;
        if (!await confirmDiscard()) return;
        if (!await requestConfirmation({ kind: 'email', title: 'Enviar petición de factura',
            message: 'Se enviará un enlace personal para adjuntar la factura a esta dirección:', filename: values.recipient_email,
            detail: `Asunto: ${values.subject}. El enlace caduca en 14 días y admite una entrega.`, confirmLabel: 'Confirmar envío',
        })) return;
        const key = JSON.stringify(values);
        if (invoiceRequestAttempt.current?.key !== key) invoiceRequestAttempt.current = { key, id: emailRequestId() };
        const value = await api('/invoice-requests', json('POST', { ...values, confirmed: true, request_id: invoiceRequestAttempt.current.id }));
        apply(value); setScreen('request-status');
        const status = value.invoice_request?.delivery_status;
        if (status === 'accepted') notify('Correo aceptado. La gestión queda pendiente de recibir la factura.');
        else notify(status === 'rejected' ? 'El servicio ha rechazado el correo. La gestión conserva la traza del intento.'
            : 'No se ha podido confirmar el envío. Comprueba su estado antes de generar otra solicitud.', 'amber');
    });
    const startInvoiceRequest = () => {
        invoiceRequestAttempt.current = null;
        setScreen('request');
    };
    const refreshInvoiceRequest = () => perform(async () => {
        if (!await confirmDiscard()) return;
        const value = await api(`/cases/${record.id}`); apply(value);
        setScreen(value.documents?.length ? 'source' : 'request-status');
    });
    const downloadProposalPDF = () => perform(async () => {
        const response = await authFetch(`${ROOT}/cases/${record.id}/proposal?format=pdf`);
        if (!response.ok) {
            const result = await response.json().catch(() => ({}));
            throw new Error(result.error || 'No se ha podido descargar el PDF de la oferta.');
        }
        downloadBlob(await response.blob(), `oferta-${record.id}.pdf`);
    });
    const confirmDiscard = async (confirmLabel = 'Descartar cambios') => !dirty || requestConfirmation({
        kind: 'discard', title: 'Hay cambios sin guardar',
        message: 'Si continúas, perderás los cambios pendientes de esta gestión.',
        detail: 'La gestión y los documentos ya guardados se conservan. Puedes cancelar para seguir editando.',
        filename: record?.titulo, confirmLabel,
    });
    const clearCurrentCase = () => {
        setRecord(null); setDraft(EMPTY_INVOICE); setDirty(false); setTitle('');
        setReferenceMode('total'); setPeriodMode('fechas'); setReviewAttempted(false);
        setFields({});
    };
    const leave = onLeave => perform(async () => {
        if (!await confirmDiscard('Salir sin guardar')) return;
        if (dirty || !onLeave) { clearCurrentCase(); setScreen('home'); }
        onLeave?.();
    });
    const archiveCase = (item, archived = !item.archived_at) => perform(async () => {
        if (!await requestConfirmation({
            kind: 'archive', title: archived ? 'Archivar gestión' : 'Restaurar gestión', filename: item.titulo,
            message: archived ? 'La gestión dejará de aparecer en los listados habituales. Se conservarán sus documentos, datos, comparativas y ofertas.'
                : 'La gestión volverá a aparecer en los listados habituales.',
            detail: archived ? `${record?.id === item.id && dirty ? 'Los cambios pendientes sin guardar se descartarán. ' : ''}Podrás encontrarla con «Incluir archivadas» y restaurarla cuando quieras.` : 'Se conservarán todos sus datos guardados.',
            confirmLabel: archived ? 'Archivar' : 'Restaurar',
        })) return;
        const saved = await api(`/cases/${item.id}/archive`, json('POST', { version: item.version, archived }));
        if (record?.id === item.id) {
            if (archived) { clearCurrentCase(); setScreen('cases'); }
            else setRecord(previous => ({ ...previous, ...saved, reviewed_input: previous.reviewed_input }));
        }
        notify(archived ? 'Gestión archivada correctamente.' : 'Gestión restaurada correctamente.');
        reloadCases();
    });
    const renameCase = (item, name) => perform(async () => {
        const value = await api(`/cases/${item.id}`, json('PATCH', { version: item.version, titulo: name }));
        // Renaming must not apply a saved review over the user's pending edits.
        setRecord(previous => previous?.id === item.id ? { ...previous, titulo: value.titulo, version: value.version, updated_at: value.updated_at,
            created_by: value.created_by, updated_by: value.updated_by, channel_id: value.channel_id, participated_by_me: value.participated_by_me } : previous);
        updateCaseSummary(value);
        notify('Nombre de la gestión actualizado.');
        return true;
    });
    const openCase = (id, destination) => perform(async () => {
        if (id === record?.id && (dirty || !record.invoice_request)) { setScreen(dirty ? 'review' : destination); return; }
        if (!await confirmDiscard()) return;
        const value = await api(`/cases/${id}`); apply(value);
        setScreen(value.invoice_request && !value.documents?.length ? 'request-status' : destination === 'offers' ? value.comparison ? 'offers' : 'review' : destination);
    });
    const compatibleToll = ELECTRIC_TOLLS.includes(draft.tarifa_atr);
    const gas = draft.tipo_suministro === 'gas' || /^RL/i.test(draft.tarifa_atr || '');
    const periodCount = compatibleToll ? draft.tarifa_atr === '2.0TD' ? 3 : 6 : 0;
    const powerCount = compatibleToll ? draft.tarifa_atr === '2.0TD' ? 2 : 6 : 0;
    const activeDocumentId = record?.active_document_id || record?.extraction?.document_id;
    const activeDocument = record?.documents.find(doc => doc.id === activeDocumentId);
    const pendingReading = Boolean(record?.documents.length && (!record.extraction || (activeDocumentId && record.extraction.document_id !== activeDocumentId)));
    const needsCatalog = enabled && open && (['type', 'source'].includes(screen) || (record && !gas && !pendingReading && ['review', 'offers'].includes(screen)));
    const [newSimulationZone, setNewSimulationZone] = useState('PENINSULA');
    const catalogZone = ['type', 'source'].includes(screen) && (!record || pendingReading || gas)
        ? newSimulationZone : draft.geo_zone || 'PENINSULA';
    const tariffCatalog = useInvoiceTariffCatalog(api, Boolean(needsCatalog), catalogZone);
    useEffect(() => {
        if (tariffCatalog.status === 'error') notify(tariffCatalog.error, 'red');
        else if (tariffCatalog.ready && !tariffCatalog.groups.length) {
            notify('No hay tarifas eléctricas vigentes para esta zona en el catálogo autorizado de tu canal.', 'amber');
        }
    }, [tariffCatalog.status, tariffCatalog.error, tariffCatalog.ready, tariffCatalog.groups.length, notify]);
    const reviewState = invoiceReviewState(draft, referenceMode, periodMode);
    const focusField = key => {
        const focus = () => {
            const controls = [...(rootRef.current?.querySelectorAll('[data-invoice-field]') || [])];
            const control = controls.find(element => element.dataset.invoiceField === key || element.dataset.invoiceField.startsWith(`${key}.`))
                || controls.find(element => element.dataset.invoiceField === 'tarifa_atr');
            control?.focus(); control?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        };
        if (key === 'dias_facturados' && periodMode !== 'dias') { setPeriodMode('dias'); requestAnimationFrame(focus); }
        else focus();
    };
    const validateForComparison = () => {
        setReviewAttempted(true);
        if (reviewState.complete) return true;
        setFields(reviewState.issues);
        focusField(Object.keys(reviewState.issues)[0]);
        return false;
    };
    const field = (key, label, render) => {
        const issue = reviewState.issues[key];
        const error = fields[key] || (reviewAttempted ? issue : null);
        const required = reviewState.required.has(key);
        const hintId = `invoice-hint-${key.replaceAll('.', '-')}`;
        return <label key={key} className={labelClass}>{label}{required && <span className="esave-required" aria-hidden="true"> *</span>}
            {render({ 'data-invoice-field': key, 'aria-required': required, 'aria-invalid': Boolean(error), 'data-incomplete': Boolean(issue), 'aria-describedby': error || issue ? hintId : undefined })}
            {(error || issue) && <span id={hintId} className={error ? 'esave-field-error' : 'esave-field-pending'}>{error || issue}</span>}
        </label>;
    };
    const numeric = (key, label, period) => {
        const value = period ? draft[key]?.[period] : draft[key];
        const fieldKey = period ? `${key}.${period}` : key;
        return field(fieldKey, label, props => <input {...props} type="number" min="0" step="any" className={inputClass} value={value ?? ''}
            onChange={event => change(key, event.target.value, period)} />);
    };

    const readingWarnings = [...(record?.extraction?.result.validation_errors || []), ...(record?.extraction?.result.warnings || [])];

    return { enabled, open, setOpen, cases, setCases, record, setRecord, draft, setDraft, title, setTitle, dirty, setDirty,
        screen, setScreen, caseScope, caseList, casePagination, changeCaseList, showCases, loading, refreshCases, reloadCases, openCase, renameCase, confirmDiscard, leave, archiveCase, downloadBlob, tariffCatalog, newSimulationZone, setNewSimulationZone, confirmation, requestConfirmation, resolveConfirmation,
        busy, notifications, dismissNotification, fields, pendingAttachment, setPendingAttachment, rootRef, api, json, apply, perform, change, save,
        readDocument, upload, download, sendProposal, requestInvoice, startInvoiceRequest, refreshInvoiceRequest, downloadProposalPDF, compatibleToll, gas, periodCount, powerCount, activeDocumentId, activeDocument, pendingReading, numeric, readingWarnings, authFetch,
        field, reviewState, focusField, validateForComparison, referenceMode, chooseReferenceMode, periodMode, choosePeriodMode, changePeriod };
}
